/**
 * Zod schema for the `surveyResponses` collection (ADR-054, ADR-067, ADR-136).
 *
 * One response per (person, survey), composite ID `{personId}_{surveyId}`.
 * Immutable after creation. Anonymous surveys (all surveys in v1) keep
 * `personId` for dedup only; it is never surfaced in admin views and
 * server-side aggregation (`getSurveyResults`) excludes it from the
 * response payload.
 *
 * Multi-question shape: each response carries `answers[]`, one entry per
 * question on the parent survey, joined by `questionId`.
 */

import { z } from 'zod';
import type { TimestampLike } from '../types/common';

export const surveyAnswerSchema = z.object({
  /** Joins back to `Survey.questions[].id`. */
  questionId: z.string().min(1),
  /**
   * Response payload — exactly one is populated based on the question's type.
   *  - single_choice → `selectedOptionIndex` (0-based into question.options)
   *  - free_text     → `freeTextResponse`
   *  - rating        → `rating` (1–5)
   */
  selectedOptionIndex: z.number().nullable(),
  freeTextResponse: z.string().nullable(),
  rating: z.number().nullable(),
});

export const surveyResponseDocumentSchema = z.object({
  surveyId: z.string(),
  eventId: z.string(),
  /** Denormalized from survey for cascade delete queries. */
  sessionId: z.string().nullable(),
  /** Stored for dedup. Not displayed for anonymous surveys. */
  personId: z.string(),

  /** One answer per question on the parent survey. */
  answers: z.array(surveyAnswerSchema).min(1),

  submittedAt: z.custom<TimestampLike>(),
});
